import React, { useState } from "react";

const FORM_NAME = "contact";
const HONEYPOT_NAME = "bot-field";
const teal = "#00ffe0";

const placeholderStyle = `
  ::placeholder { color: ${teal}; opacity: 1; }
  input, textarea { color-scheme: dark; }
`;

const honeypotStyle = {
  position: "absolute",
  left: "-9999px",
  top: "auto",
  width: "1px",
  height: "1px",
  overflow: "hidden",
  opacity: 0,
  pointerEvents: "none",
  tabIndex: -1,
};

function isBlank(value) {
  return !String(value || "").trim();
}

export default function ContactForm() {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const form = e.target;
    const data = new FormData(form);
    data.set("form-name", FORM_NAME);

    const name = String(data.get("name") || "").trim();
    const email = String(data.get("email") || "").trim();
    const message = String(data.get("message") || "").trim();
    const honeypot = String(data.get(HONEYPOT_NAME) || "").trim();

    // Bots that fill the honeypot: pretend success, never notify Netlify.
    if (honeypot) {
      setSent(true);
      form.reset();
      setLoading(false);
      return;
    }

    if (isBlank(name) || isBlank(email) || isBlank(message)) {
      setError("Please fill in your name, email, and message.");
      setLoading(false);
      return;
    }

    data.set("name", name);
    data.set("email", email);
    data.set("message", message);

    try {
      const res = await fetch("/", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: data,
      });
      if (res.ok) {
        setSent(true);
        form.reset();
      } else {
        const json = await res.json().catch(() => ({}));
        setError(json.error || "Something went wrong. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <form
      name={FORM_NAME}
      method="POST"
      data-netlify="true"
      data-netlify-honeypot={HONEYPOT_NAME}
      onSubmit={handleSubmit}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 20,
        minWidth: 300,
        color: "#fff",
        fontFamily: "Poppins, Inter, Segoe UI, Arial, sans-serif",
        position: "relative",
      }}
      autoComplete="off"
    >
      <input type="hidden" name="form-name" value={FORM_NAME} />
      {/* Netlify honeypot — leave empty; bots often fill it */}
      <p style={honeypotStyle} aria-hidden="true">
        <label>
          Don’t fill this out if you’re human:{" "}
          <input type="text" name={HONEYPOT_NAME} tabIndex={-1} autoComplete="off" />
        </label>
      </p>
      <style>{placeholderStyle}</style>
      <label style={{ fontWeight: 700, fontSize: 18, color: teal }}>
        Contact Drum Sircle
      </label>
      <input
        type="text"
        name="name"
        required
        placeholder="Your Name"
        style={{
          background: "rgba(0,0,0,0.6)",
          border: `1.5px solid ${teal}`,
          color: "#fff",
          borderRadius: 9,
          padding: "12px 15px",
          fontSize: 16,
          outline: "none"
        }}
        autoComplete="off"
      />
      <input
        type="email"
        name="email"
        required
        placeholder="Your Email"
        style={{
          background: "rgba(0,0,0,0.6)",
          border: `1.5px solid ${teal}`,
          color: "#fff",
          borderRadius: 9,
          padding: "12px 15px",
          fontSize: 16,
          outline: "none"
        }}
        autoComplete="off"
      />
      <textarea
        name="message"
        required
        maxLength={1000}
        placeholder="Your Message (max 1000 characters)"
        style={{
          background: "rgba(0,0,0,0.6)",
          border: `1.5px solid ${teal}`,
          color: "#fff",
          borderRadius: 9,
          padding: "12px 15px",
          fontSize: 16,
          outline: "none",
          minHeight: 120
        }}
      />
      <button
        type="submit"
        disabled={loading}
        style={{
          background: teal,
          color: "#111",
          border: "none",
          borderRadius: 22,
          padding: "13px 0",
          fontWeight: 700,
          fontSize: 17,
          cursor: loading ? "wait" : "pointer",
          boxShadow: "0 0 12px #00ffe077",
          marginTop: 4
        }}
      >
        {loading ? "Sending..." : sent ? "Sent! Thank you!" : "Send"}
      </button>
      {error && <div style={{ color: "#f33", marginTop: 4 }}>{error}</div>}
    </form>
  );
}
